import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/cn";
import { formatMinutes, parseTimeToMinutes } from "./slotProjection";

interface Props {
  /** "HH:mm", or null while the field is blank. */
  start: string | null | undefined;
  end: string | null | undefined;
  onChange: (next: { start: string; end: string }) => void;
  disabled?: boolean;
}

const STEP_MINUTES = 15;
const DAY_MINUTES = 24 * 60;
/** Handles may not cross; the window always keeps room for one step. */
const MIN_SPAN = STEP_MINUTES;

const HOUR_TICKS = [0, 4, 8, 12, 16, 20, 24];

/** "HH:mm" for a minutes-from-midnight value. */
function toHHMM(total: number): string {
  const clamped = Math.max(0, Math.min(DAY_MINUTES, total));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * Dual-handle availability window slider.
 *
 * The two time fields stay the precise input; this is the one that makes the
 * window feel like a thing you can grab. Both edit the same values, so dragging
 * updates the fields and typing moves the handles.
 */
export function TimeWindowSlider({ start, end, onChange, disabled = false }: Props) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<"start" | "end" | null>(null);

  // A blank window still needs somewhere to put the handles; 10:00–17:00 is the
  // server's own fallback, so the slider opens where an unconfigured property
  // would actually land.
  const startMinutes = parseTimeToMinutes(start) ?? 10 * 60;
  const endMinutes = parseTimeToMinutes(end) ?? 17 * 60;

  const commit = useCallback(
    (handle: "start" | "end", minutes: number) => {
      const snapped = Math.round(minutes / STEP_MINUTES) * STEP_MINUTES;
      if (handle === "start") {
        const next = Math.max(0, Math.min(snapped, endMinutes - MIN_SPAN));
        onChange({ start: toHHMM(next), end: toHHMM(endMinutes) });
      } else {
        const next = Math.min(DAY_MINUTES, Math.max(snapped, startMinutes + MIN_SPAN));
        onChange({ start: toHHMM(startMinutes), end: toHHMM(next) });
      }
    },
    [startMinutes, endMinutes, onChange],
  );

  const minutesFromClientX = useCallback((clientX: number) => {
    const track = trackRef.current;
    if (!track) return 0;
    const rect = track.getBoundingClientRect();
    const ratio = (clientX - rect.left) / rect.width;
    return Math.max(0, Math.min(1, ratio)) * DAY_MINUTES;
  }, []);

  // Dragging is tracked on the window so the pointer can leave the track without
  // the handle sticking.
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: PointerEvent) => commit(dragging, minutesFromClientX(e.clientX));
    const onUp = () => setDragging(null);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [dragging, commit, minutesFromClientX]);

  const onKeyDown = (handle: "start" | "end") => (e: React.KeyboardEvent) => {
    const current = handle === "start" ? startMinutes : endMinutes;
    const big = e.key === "PageUp" || e.key === "PageDown";
    const delta =
      e.key === "ArrowRight" || e.key === "PageUp"
        ? STEP_MINUTES * (big ? 4 : 1)
        : e.key === "ArrowLeft" || e.key === "PageDown"
          ? -STEP_MINUTES * (big ? 4 : 1)
          : 0;
    if (delta === 0) return;
    e.preventDefault();
    commit(handle, current + delta);
  };

  const startPct = (startMinutes / DAY_MINUTES) * 100;
  const endPct = (endMinutes / DAY_MINUTES) * 100;

  return (
    <div className={cn("select-none", disabled && "pointer-events-none opacity-50")}>
      <div
        ref={trackRef}
        className="relative h-9"
        onPointerDown={(e) => {
          if (disabled) return;
          // Clicking the bare track moves whichever handle is nearer.
          const minutes = minutesFromClientX(e.clientX);
          const handle =
            Math.abs(minutes - startMinutes) <= Math.abs(minutes - endMinutes) ? "start" : "end";
          commit(handle, minutes);
          setDragging(handle);
        }}
      >
        <div className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-bridge-light-gray" />
        <div
          className="absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-accent-gradient"
          style={{ left: `${startPct}%`, width: `${Math.max(0, endPct - startPct)}%` }}
        />

        {(["start", "end"] as const).map((handle) => {
          const pct = handle === "start" ? startPct : endPct;
          const minutes = handle === "start" ? startMinutes : endMinutes;
          return (
            <div
              key={handle}
              role="slider"
              tabIndex={disabled ? -1 : 0}
              aria-label={handle === "start" ? "Window start" : "Window end"}
              aria-valuemin={0}
              aria-valuemax={DAY_MINUTES}
              aria-valuenow={minutes}
              aria-valuetext={formatMinutes(minutes)}
              onPointerDown={(e) => {
                if (disabled) return;
                e.stopPropagation();
                setDragging(handle);
              }}
              onKeyDown={onKeyDown(handle)}
              className={cn(
                "absolute top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full border-2 border-white bg-bridge-accent shadow-soft outline-none transition-shadow",
                "focus-visible:ring-2 focus-visible:ring-bridge-accent/50",
                dragging === handle && "cursor-grabbing ring-2 ring-bridge-accent/40",
              )}
              style={{ left: `${pct}%` }}
            >
              {/* Time read-out, revealed while the handle is being dragged. */}
              <span
                data-show={dragging === handle}
                className="pointer-events-none absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-bridge-fg px-1.5 py-0.5 text-[10px] font-medium text-white opacity-0 transition-opacity data-[show=true]:opacity-100"
              >
                {formatMinutes(minutes)}
              </span>
            </div>
          );
        })}
      </div>

      <div className="relative h-4">
        {HOUR_TICKS.map((hour) => (
          <span
            key={hour}
            className="absolute -translate-x-1/2 text-[9px] tabular-nums text-bridge-subtle"
            style={{ left: `${(hour / 24) * 100}%` }}
          >
            {hour === 24 ? "12 AM" : formatMinutes(hour * 60).replace(":00", "")}
          </span>
        ))}
      </div>
    </div>
  );
}
